import React, { useState, useEffect, Fragment, useMemo } from "react";
import { useRouteMatch, useLocation } from "react-router-dom";
import { useForm, SubmitHandler } from "react-hook-form";
import ApiCaller from "api/apiCaller";
import api from "api";
import {
  Button,
  Input,
  Col,
  Row,
  Container,
  Label,
  FormGroup,
} from "reactstrap";
import TextareaAutosize from "react-textarea-autosize";

import { DashboardSetup, DashboardTemplate } from "model/dashboard";
import "./index.css";
import Loading from "components/Loading";
import * as utils from "../../../../resources/utils"

interface DatasourcesFilter {
  id: number,
  name: string
};

export default function DashboardEdit() {
	const companyId: any = api.getCurrentCompanyId();
  const baseRoute = 'monitoring/dashboard'
  const match = useRouteMatch(`/${baseRoute}/setup/:action/:dashboard_id`);
  const params: any = match?.params;
  const dashboardId = params?.dashboard_id;
  const action = params?.action;
	const [loading, setLoading] = useState<boolean>(true);

	const defaultDashboard:DashboardSetup = {

	};

	const [dashboard, setDashboard] = useState<DashboardSetup>({
		...defaultDashboard,
	});
  const [dashboardDataSetupStatus, setDashboardDataSetupStatus] = useState(false);
  const [metabaseDashboardSetupStatus, setMetabaseDashboardSetupStatus] = useState(false);
	const [isCreate, setIsCreate] = useState(true);
	const [isSaving, setIsSaving] = useState(false);
	const [dashboardTemplate, setDashboardTemplate] = useState([]);
	const [datasources, setDatasources] = useState<Array<DatasourcesFilter>>([]);

	const { register, setValue, handleSubmit, formState: { errors } } = useForm<DashboardSetup>({
    mode: 'all',
    reValidateMode: 'onChange',
    defaultValues: dashboard
  });

	const getDashboardTemplate = async () => {
		let uri = `${baseRoute}/templates`;
		const res:any = await ApiCaller.get(uri);
		if (res.data && res.data.dashboard_templates) {
			setDashboardTemplate(res.data.dashboard_templates);
		}
	};

	const getDatasources = async () => {
    const res:any = await ApiCaller.get(
      `${baseRoute}/datasources?company_id=${companyId}`
    );
    if (res.data && res.data.datasources) {
      setDatasources(res.data.datasources);
    }
  };

	const getDashboardSetup = async () => {
		let uri = `${baseRoute}/${dashboardId}/setup?company_id=${companyId}`;
		const res:any = await ApiCaller.get(uri);
		if (res.data && res.data.dashboard_setup) {
			setDashboard(res.data.dashboard_setup);
      setDatasources(
        [...datasources,
          {id: res.data.dashboard_setup.datasource_id, name: res.data.dashboard_setup.datasource_name}
        ]
      )
		}
		setLoading(false);
	};

  const setupDashboardData = async (dashboardId: any) => {
    const method = dashboard.id ? 'put' : 'post';
    const uri = `${baseRoute}/setup/data`;
    let payload = {
      'dashboard_id': dashboardId
    }
		const res:any = await ApiCaller[method](`${uri}?company_id=${companyId}`, payload);
    if (res.status === 'ok') {
      return true
    }
  }

  const setupMetabaseDashboard = async (dashboardId: any) => {
    const method = dashboard.id ? 'put' : 'post';
    const uri = `${baseRoute}/setup/metabase_dashboard`;
    let payload = {
      'dashboard_id': dashboardId
    }
		const res:any = await ApiCaller[method](`${uri}?company_id=${companyId}`, payload);
    if (res.status === 'ok') {
      return true
    }
  }

	const saveDashboardSetup = async () => {
		setIsSaving(true);
		const method = dashboard.id ? 'put' : 'post';
		const uri = dashboard.id ? `${baseRoute}/${dashboard.id}/setup` : `${baseRoute}/setup`;
		const res:any = await ApiCaller[method](`${uri}?company_id=${companyId}`, dashboard);

		if (res.status === 'ok') {
      const dataSetupStatus = await setupDashboardData(res.data.dashboard_id);

      // set current datasource_id to cookies
      utils.default.setCookies('datasource_id', dashboard.datasource_id);

      if (dataSetupStatus) {
        setDashboardDataSetupStatus(true);
        const metabaseSetupStatus = await setupMetabaseDashboard(res.data.dashboard_id);
        if (metabaseSetupStatus) {
          setMetabaseDashboardSetupStatus(true)
          setIsSaving(false);
        }
      }
		} else {
      alert(res.message);
		}
	};

  const goToDashboard = async () => {
    console.log(isSaving);
    console.log(dashboardDataSetupStatus)
    if (!isSaving && dashboardDataSetupStatus && metabaseDashboardSetupStatus) {
      window.location.href = `/${baseRoute}`;
    }
  }

	const setDashboardSetupAttr = (attr:any, value:any, customAttrKey:any) => {
		if (!customAttrKey) {
			setDashboard({
				...dashboard,
				[attr]: value,
			});
		} else {
			setDashboard({
				...dashboard,
				[customAttrKey]: {
					// @ts-expect-error
					...dashboard[customAttrKey],
					[attr]: value,
				},
			});
		}
	};

	const onSubmit = handleSubmit(data => {
    saveDashboardSetup();
  });

	const renderLayoutItem = (layoutItem:any) => {
    const shouldDisableInput = action === 'view';
    let componentLabel = layoutItem.label || layoutItem.attribute;
    componentLabel = componentLabel.replaceAll('_', ' ');
    // @ts-expect-error
    let componentValue = dashboard[layoutItem.attribute];
    if (layoutItem.customAttrKey) {
      // @ts-expect-error
      componentValue = dashboard[layoutItem.customAttrKey][layoutItem.attribute];
    }
    if (layoutItem.inputType === 'textarea' && layoutItem.isShow) {
      return (
        <FormGroup className={layoutItem.className}>
          <Label className="dashboard-detail__layout-item__label">{componentLabel}</Label>
          {layoutItem.tooltip &&
            utils.default.renderTooltip(layoutItem.tooltip)
          }
          <TextareaAutosize
            {...register(
              layoutItem.customAttrKey ? `${layoutItem.customAttrKey}.${layoutItem.attribute}` : layoutItem.attribute,
              layoutItem.validation
            )}
            className="form-control"
            onChange={e => setDashboardSetupAttr(layoutItem.attribute, e.target.value, layoutItem.customAttrKey)}
            value={componentValue || ""}
            minRows={3}
          />
          {layoutItem.error}
        </FormGroup>
      )
    };
    if (layoutItem.inputType === 'select' && layoutItem.isShow) {
      return (
        <FormGroup className={layoutItem.className}>
          <Label className="dashboard-detail__layout-item__label">{componentLabel}</Label>
          {layoutItem.tooltip &&
            utils.default.renderTooltip(layoutItem.tooltip)
          }
          <select
            {...register(
              layoutItem.customAttrKey ? `${layoutItem.customAttrKey}.${layoutItem.attribute}` : layoutItem.attribute,
              componentValue ? '' : layoutItem.validation
            )}
            className="form-control"
            onChange={e => setDashboardSetupAttr(layoutItem.attribute, e.target.value, layoutItem.customAttrKey)}
            value={componentValue || {}}
            disabled={shouldDisableInput || layoutItem.isDisabledAfterCreate}
          >
            <option value="" hidden>-- Select one --</option>
            {layoutItem.options.map((option:any) =>
              <option key={option.value || option.id} value={option.value || option.id}>
                {option.label || option.name}
              </option>
            )}
          </select>
          {layoutItem.error}
        </FormGroup>
      )
    };
    if (layoutItem.inputType === "checkbox" && layoutItem.isShow) {
      return (
        <FormGroup className={layoutItem.className}>
          <Label className="dashboard-detail__layout-item__label">{componentLabel}</Label>{' '}
          {layoutItem.tooltip &&
            utils.default.renderTooltip(layoutItem.tooltip)
          }
          <input
            {...register(
              layoutItem.customAttrKey ? `${layoutItem.customAttrKey}.${layoutItem.attribute}` : layoutItem.attribute,
              layoutItem.validation
            )}
            type="checkbox"
            onChange={e => setDashboardSetupAttr(layoutItem.attribute, e.target.checked, layoutItem.customAttrKey)}
            value={componentValue || false}
            checked={componentValue}
          />
          {layoutItem.error}
        </FormGroup>
      )
    };
    if (layoutItem.isShow) {
      return (
        <FormGroup className={layoutItem.className}>
          <Label className="dashboard-detail__layout-item__label">{componentLabel}</Label>
          {layoutItem.tooltip &&
            utils.default.renderTooltip(layoutItem.tooltip)
          }
          <input
            {...register(
              layoutItem.customAttrKey ? `${layoutItem.customAttrKey}.${layoutItem.attribute}` : layoutItem.attribute,
              layoutItem.validation
            )}
            className="form-control"
            onChange={e => setDashboardSetupAttr(layoutItem.attribute, e.target.value, layoutItem.customAttrKey)}
            value={componentValue || ""}
          />
          {layoutItem.error}
        </FormGroup>
      );
    }
  };

	const renderLayout = [
		{
      groupId: 1,
      name: 'Dashboard Configuration',
      renderCondition: true,
      layoutItems: [
				{ attribute: 'dashboard_template_id', label: 'Dashboard Template', inputType: 'select', className: 'required-field',
          isShow: true,
          options: dashboardTemplate,
          isDisabledAfterCreate: action === 'edit' ? true : false,
          validation: { required: true},
          error: errors?.dashboard_template_id && <p className="dashboard__validation_error">Dashboard template required</p>
        },

        { attribute: 'datasource_id', label: 'Project', inputType: 'select', className: 'required-field',
          isShow: true,
          options: datasources,
          isDisabledAfterCreate: action === 'edit' ? true : false,
          validation: { required: true},
          error: errors?.datasource_id && <p className="dashboard__validation_error">Project required</p>
        },

        { attribute: 'source_dataset', className: 'required-field',
          isShow: dashboard.datasource_id ? true : false,
          tooltip: 'Dataset where source table is located',
          validation: { required: true},
          error: errors?.source_dataset && <p className="dashboard__validation_error">Dataset required</p>
        },
      ],
    },
	];

	useEffect(() => {
		if (action && action === 'create') {
			setIsCreate(true);
			setLoading(false);
		} else {
			setIsCreate(false);
		}
	}, [action]);

	useEffect(() => {
		if (dashboardId && !isCreate) {
			getDashboardSetup();
		}
	}, [dashboardId, isCreate]);

	useEffect(() => {
		if (isCreate) {
			setDashboard({
				...dashboard,
				company_id: companyId,
			});
		}
	}, [isCreate]);

	useEffect(() => {
		getDatasources();
		getDashboardTemplate();
	}, []);

  useEffect(() => {
    goToDashboard();
  }, [isSaving, dashboardDataSetupStatus, metabaseDashboardSetupStatus])

	// display loading
  if (loading) return <Loading />;

	return (
		<Container>
      <Row>
        <Col xs={12}>
          <h1>{isCreate ? `Setup new dashboard` : `${dashboard.name}`}</h1>
        </Col>
				<form className="dashboard__form" onSubmit={onSubmit}>
					{(action === 'create' || action === 'edit') &&
						<Button
							className="dashboard__submit_button"
							type="submit"
							value="submit"
							color={isSaving ? 'secondary' : 'success'}
							disabled={isSaving}
						>
              {isCreate ? 'Create': 'Save'}
						</Button>
					}
					{renderLayout.map(layoutGroup => (
						<Fragment key={layoutGroup.groupId}>
							{layoutGroup.renderCondition &&
								<div className="dashboard-detail__layout-group">
									<div className="dashboard-detail__layout-group__name">{layoutGroup.name}</div>
									<div className="dashboard-detail__layout-group__components">
										{layoutGroup.layoutItems.map(layoutItem => (
											<div key={layoutItem.attribute}>
												{renderLayoutItem(layoutItem)}
											</div>
										))}
									</div>
								</div>
							}
						</Fragment>
					))}
				</form>
      </Row>
      {(isSaving) && 
        <div className="dashboard-setup__display-process">
          <Row className="dashboard-setup__process">
            {dashboardDataSetupStatus ?
              <span>Dashboard data setup successfully {utils.default.renderStatusIcon('success')}</span> :
              <span>Setting up dashboard data... {utils.default.renderStatusIcon('running')}</span>
            }
          </Row>
          <Row className="dashboard-setup__process">
            {metabaseDashboardSetupStatus ?
              <span>Dashboard setup successfully {utils.default.renderStatusIcon('success')}</span>:
              <span>Setting up dashboard... {utils.default.renderStatusIcon('running')}</span>
            }
          </Row>

        </div>
      }
    </Container>
	);
}